import React from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Link, Divider, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface ChangelogProps {
  open: boolean;
  onClose: () => void;
}

const Changelog: React.FC<ChangelogProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const dialogBg = theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff';
  const textColor = theme.palette.text.primary;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ p: 3, pb: 1, fontWeight: 800, color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: dialogBg }}>
        Changelog
        <IconButton onClick={onClose} sx={{ ml: 2 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, md: 4 }, background: dialogBg, color: textColor }}>
        <Typography sx={{ fontSize: 16, color: textColor, mb: 3 }}>
          All notable changes to the AutoUpgrade Composer will be documented in this file.
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.2-6] - 2026-03-12 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Patch Content optional version placeholders now reflect the selected target version across all execution modes: <b>19.30</b> for Oracle 19c, <b>21.20</b> for Oracle 21c, and <b>23.26.1</b> for Oracle 23c/26ai</li>
          <li><b>OJVM</b> option in Patch Content is now disabled when target version is 21, 23, or 26 — OJVM is already included in the Release Update for those versions</li>
          <li><b>SDO (SDOBP)</b> option in Patch Content is temporarily disabled for 21c/23c/26ai — will be released on AutoUpgrade version 26.3</li>
          <li><b>TEXT</b> option in Patch Content is now disabled when target version is 21, 23, or 26 — Oracle Text is already included in the Release Update for those versions</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.2-5] - 2026-03-03 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.error.main }}>
          Bug Fixes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Fixed an issue where <b>Standard Edition 2 (SE2)</b> was incorrectly available as an edition option when Oracle 26ai or Oracle 23c was selected as the target version. Since SE2 is not available in Oracle 23c and later, the option is now properly disabled when target version 23 or 26 is selected</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.2-4] - 2026-02-26 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added a new <b>Home Settings</b> tab for Analyze, Fixups, Deploy, and Create Home execution modes, exposing Oracle home installation parameters previously not available in the Composer. The tab includes: Oracle Home User (Windows account type), Oracle Base, Edition, Inventory Location, Inventory Group, Home Name, OPatch Conflict Handling, Read-Only Home, Apply RU (ru_apply), TMP Directory (env=TMP), and OS Groups (OSDBA, OSOPER, OSBACKUPDBA, OSDGDBA, OSKMDBA, OSRACDBA)</li>
          <li>Added <b>Binary Options</b> section within Home Settings with tri-state control (ON / OFF / UNSET) for each option: RAC, ASM, dNFS, Data Mining, JOX, OLAP, Partitioning, RAT, SDO, and Unified Auditing. UNSET follows Oracle installation defaults (most options enabled by default); ON or OFF explicitly writes the parameter to the generated config</li>
          <li>Cluster Nodes field is now shown only when RAC is explicitly set to ON</li>
          <li>Home Settings tab is positioned as the second tab for quick access, immediately after Basic Information</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.2-3] - 2026-02-11 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added the capability to automatically update the autoupgrade.jar file for customers based on their preference (AU) - as per enhancement AUPG-4948</li>
          <li>Added the option for SDO and TEXT in Patch Content. When SDO is selected, the patch parameter is appended with SDOBP; when TEXT is selected, the patch parameter is appended with TEXT - as per enhancement AUPG-4710</li>
          <li>Allow 26 as alias for 23 in target_version for create_home and download modes - as per enhancement AUPG-5117</li>
        </ul>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>OCW remains active and clickable when RECOMMENDED is selected, allowing users to optionally add OCW to the patch configuration without it being auto-checked</li>
          <li>Visual enhancements: two-column layout for Patch Content options, tooltips with descriptive text for each patch type (RU, OJVM, OPATCH, DPBP, AU, JDK, OCW, SDO, TEXT, MRP), and capitalized display for dropdown options (RAC Rolling, Gold Image, Drain Timeout)</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.2-2] - 2026-02-10 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added RAC Rolling parameter for Deploy and Analyze modes with options: DISABLED, REQUIRED, FORCE, and AUTO (default). RAC Rolling enables patching RAC databases without downtime by patching nodes one at a time while keeping the database available on other nodes - as per enhancement AUPG-4157, released on AutoUpgrade v26.1/v26.2</li>
          <li>Added Drain Timeout parameter for Deploy and Analyze modes, allowing configuration of how long AutoUpgrade should wait for sessions to drain before stopping an instance. Supports either a time value in seconds (integer greater than or equal to 0) or WAIT mode, which gives full control to the user to proceed when ready via command-line prompt - as per enhancement AUPG-4157, released on AutoUpgrade v26.1/v26.2</li>
        </ul>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added Ignore PreReq Failure switch for Create Home, Analyze, and Deploy modes. When enabled, AutoUpgrade will ignore prerequisite check failures during patching operations. This is particularly useful when a new Oracle Home is being installed or created, allowing the process to continue despite failed prerequisite checks</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.2-1] - 2026-02-09 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added Gold Image field in Download mode with options: NO, YES, ALL, and AUTO (default). The Gold Image feature allows AutoUpgrade to download pre-built Oracle Home images from Oracle Updater Service, significantly reducing download and setup time for patch operations - as per enhancement AUPG-4908, released on AutoUpgrade v26.1/v26.2</li>
          <li>Enhanced Initial Setup for Downloads section with detailed instructions for configuring keystore with MOS credentials and CSI (Customer Support Identifier), including step-by-step guidance for adding CSI to the keystore - as per enhancement AUPG-4908, released on AutoUpgrade v26.1/v26.2</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.6-5] - 2026-02-02 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Updated placeholders to use 23.26.1, 21.20, and 19.30 for Download and Create Home modes</li>
          <li>Changed placeholder to 19.30 for target_home</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.6-4] - 2026-01-30 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Changed the default path for log Oracle Home 23.26 to 23.26.1</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Divider sx={{ mb: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.6-3] - 2026-01-24 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Changed the default path for log directory</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.6-2] - 2025-12-26 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Visual Enhancements
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Overall visual enhancements for better readability</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.6-1] - 2025-10-21 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added the option to select JDK in the Patch Content as per enhancement AUPG-4836, released on AutoUpgrade v25.5</li>
          <li>Added the validation to disallow keystore and global_log_dir to use the same path as per enhancement AUPG-5007, released on AutoUpgrade v25.5</li>
          <li>Added the support the values for 21 and 23 on target_version, as per enhancement AUPG-5018, released on AutoUpgrade v25.5</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.4-3] - 2025-08-27 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added the option to use a dynamic path with %RELEASE%.%UPDATE% for AutoUpgrade Patching, allowing automatic path generation based on source home or default patterns</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.4-2] - 2025-08-05 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.error.main }}>
          Bug Fixes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Fixed an issue that was consuming excessive CPU resources in PatchSection due to infinite re-renders</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.4-1] - 2025-08-04 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added the option to specify the custom Oracle Clusterware patch level (OCW:X.Y) on AutoUpgrade Patching as per enhancement AUPG-4748 released on AutoUpgrade version v25.4, released by Oracle on 2025-07-30</li>
          <li>Added the option to specify the cluster_nodes on AutoUpgrade Patching as per enhancement AUPG-4440 released on AutoUpgrade version v25.4, released by Oracle on 2025-07-30</li>
          <li>Added the option to enable the Binary Option for RAC on AutoUpgrade Patching (feature already exists on AutoUpgrade but added on AutoUpgrade-Composer to be used along with cluster_nodes if needed)</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.3-3] - 2025-06-24 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Two new parameters added for Create Home Mode:
            <ul style={{ marginTop: 4, marginBottom: 4, marginLeft: 24, color: textColor }}>
              <li>oraInventory</li>
              <li>Inventory Group</li>
            </ul>
          </li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.3-2] - 2025-06-02 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.error.main }}>
          Bug Fixes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Fixed a bug on Start Time</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.3-1] - 2025-06-02 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          Major Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>First STABLE release with full Upgrade capability</li>
          <li>Upgrade modes supported:
            <ul style={{ marginTop: 4, marginBottom: 4, marginLeft: 24, color: textColor }}>
              <li>Regular upgrade</li>
              <li>Non-CDB to CDB (noncdb2cdb)</li>
              <li>PDB Relocation (pdbrelocation)</li>

            </ul>
          </li>
          <li>All previous patching and configuration features from Beta</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.3-b45] - 2025-05-13 (Beta)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Database configuration generator for Oracle AutoUpgrade</li>
          <li>Support for patch operations</li>
          <li>Multiple database configurations</li>
          <li>Tabbed interface for patch configurations</li>
          <li>Context-aware subtabs for different operations</li>
          <li>Proper file naming for config files based on SID</li>
          <li>Dark/Light theme with system preference detection</li>
          <li>Configuration file download</li>
          <li>Run command generation with multiple execution modes:
            <ul style={{ marginTop: 4, marginBottom: 4, marginLeft: 24, color: textColor }}>
              <li>Analyze Only</li>
              <li>Fixups</li>
              <li>Deploy (default)</li>
              <li>Download</li>
              <li>Create Home</li>
            </ul>
          </li>
          <li>Tooltips for various fields and switches for better user guidance.</li>
          <li>Enhanced config loading: loaded configs now set execution mode, download switch, and all patch fields correctly.</li>
          <li>Platform-specific configuration: Windows-specific fields and switches are now handled and displayed properly.</li>
          <li>UI/UX improvements: modernized layout, better spacing, and clearer section headers.</li>
          <li>Edition, Oracle Base, and Target Version switches are auto-enabled when present in loaded configs.</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography sx={{ fontSize: 16, color: textColor, mb: 2 }}>
          Built with <b>React</b> and <b>Material UI</b> for a modern, responsive experience.<br />
          For more information, visit{' '}
          <Link href="https://viniciusdba.com.br" target="_blank" rel="noopener" underline="hover">
            https://viniciusdba.com.br
          </Link>
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default Changelog; 